import { type NostrEvent, type NostrMetadata, NSchema as n } from '@nostrify/nostrify';
import { useQuery } from '@tanstack/react-query';
import { useCachedNostrQuery } from '@/hooks/useCachedNostrQuery';

export interface AuthorData {
  event?: NostrEvent;
  metadata?: NostrMetadata;
}

/**
 * Fetch author metadata for multiple pubkeys in a single query.
 * Uses IndexedDB caching for instant loading.
 * Returns a Map of pubkey -> AuthorData for efficient lookups.
 */
export function useAuthors(pubkeys: string[]) {
  // Deduplicate pubkeys
  const uniquePubkeys = [...new Set(pubkeys.filter(Boolean))];

  const { data: events = [], ...queryState } = useCachedNostrQuery({
    filters: uniquePubkeys.length > 0
      ? [{ kinds: [0], authors: uniquePubkeys, limit: uniquePubkeys.length }]
      : [],
    timeout: 3000,
    cacheMaxAge: 5 * 60 * 1000, // Cache metadata for 5 minutes
    useStaleWhileRevalidate: true,
    queryOptions: {
      enabled: uniquePubkeys.length > 0,
    },
  });

  // Transform events into a Map
  return useQuery<Map<string, AuthorData>>({
    queryKey: ['authors-map', uniquePubkeys.sort().join(',')],
    enabled: uniquePubkeys.length > 0,
    queryFn: () => {
      const authorsMap = new Map<string, AuthorData>();

      // Initialize map with empty entries for all pubkeys
      for (const pubkey of uniquePubkeys) {
        authorsMap.set(pubkey, {});
      }

      // Process events and update map
      for (const event of events) {
        // Only use the most recent event per pubkey
        const existing = authorsMap.get(event.pubkey);
        if (existing?.event && existing.event.created_at > event.created_at) {
          continue;
        }

        try {
          const metadata = n.json().pipe(n.metadata()).parse(event.content);
          authorsMap.set(event.pubkey, { metadata, event });
        } catch {
          authorsMap.set(event.pubkey, { event });
        }
      }

      return authorsMap;
    },
    staleTime: Infinity, // Map transformation is stable
    gcTime: 5 * 60 * 1000,
  });
}
